<template>
  <div>
    <expansion-panels
      v-model="selected"
      :items="uploadGroups"
      value-prop="id"
      keep-activator
      multiple
    >
      <template #activator="{ item: uploadGroup, isActive }">
        <upload-group-list-item-header
          :upload-group="uploadGroup"
          :active="isActive"
          :downloading="
            downloadingUploadGroupIds.includes(uploadGroup.id)
          "
          :deleting="deletingUploadGroupIds.includes(uploadGroup.id)"
          :can-delete="canDelete"
          @download-all="onDownloadUploadGroup(uploadGroup.id)"
          @delete="confirmDeleteUploadGroup(uploadGroup.id)"
        />
      </template>
      <template #content="{ item: uploadGroup }">
        <div class="px-4 pb-4">
          <upload-list
            :reference-files="uploadGroup.referenceFiles"
            :downloading-reference-file-ids="
              downloadingReferenceFileIds
            "
            :processing-duplicates="processingDuplicates"
            :has-uploads-in-progress="hasUploadsInProgress"
            @download="onDownloadUpload"
            @download-invalid="onDownloadInvalid"
            @re-run-duplicates="onReRunDuplicates"
          />
        </div>
      </template>
    </expansion-panels>
    <v-dialog v-model="deleteDialogOpen" max-width="400">
      <v-card>
        <v-card-title class="headline">Confirm Deletion</v-card-title>
        <v-card-text>
          Are you sure you want to delete this upload group and all
          associated files? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" text @click="cancelDeleteUploadGroup"
            >Cancel</v-btn
          >
          <v-btn color="error" text @click="onDeleteUploadGroup"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import ExpansionPanels from '@/components/Common/ExpansionPanels';
import UploadGroupListItemHeader from '@/components/UploadsPage/UploadGroupListItemHeader';
import UploadList from '@/components/UploadsPage/UploadList';
import { UploadGroupDto } from '@/models';
import { typeHelpers } from '@/modules';
import { reportError } from '@/modules/errorReporting';
import { apolloClient } from '@/plugins/apollo';
import {
  ExportUpload,
  ExportUploadGroup,
  ExportInvalidRecords,
  ProcessDuplicates,
} from '@/graphql';
import { REFETCH_AFTER_DUPLICATE_CHANGE } from '@/store/modules/uploadReferences';

export default {
  name: 'UploadGroupList',
  components: {
    ExpansionPanels,
    UploadGroupListItemHeader,
    UploadList,
  },
  props: {
    reviewId: {
      type: String,
      required: true,
    },
    uploadGroups: {
      type: Array,
      default: () => [],
      validator: typeHelpers.isArrayOfType(UploadGroupDto),
    },
    // Whether the current user may delete uploads from this review. The server
    // enforces this too; this only keeps the button out of a viewer's way.
    canDelete: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    selected: [],
    downloadingUploadGroupIds: [],
    downloadingReferenceFileIds: [],
    deletingUploadGroupIds: [],
    deleteConfirmId: null,
    deleteDialogOpen: false,
    processingDuplicates: false,
    snackbar: {
      show: false,
      message: '',
      color: '',
      timeout: 5000,
    },
  }),
  computed: {
    hasUploadsInProgress() {
      return this.uploadGroups.some((uploadGroup) =>
        (uploadGroup.referenceFiles || []).some((referenceFile) =>
          referenceFile.inProgress()
        )
      );
    },
    disableReRun() {
      return this.processingDuplicates || this.hasUploadsInProgress;
    },
  },
  methods: {
    async onDownloadUpload(referenceFileId) {
      this.downloadingReferenceFileIds.push(referenceFileId);
      try {
        const response = await apolloClient.query({
          query: ExportUpload,
          variables: {
            referenceFileId,
          },
          fetchPolicy: 'network-only',
        });
        const { signedDownloadUrl } = response.data.exportUpload;
        window.location.href = signedDownloadUrl;
      } finally {
        this.downloadingReferenceFileIds = this.downloadingReferenceFileIds.filter(
          (id) => id !== referenceFileId
        );
      }
    },
    async onDownloadUploadGroup(uploadGroupId) {
      this.downloadingUploadGroupIds.push(uploadGroupId);
      try {
        const response = await apolloClient.query({
          query: ExportUploadGroup,
          variables: {
            uploadGroupId,
          },
          fetchPolicy: 'network-only',
        });
        const { signedDownloadUrl } = response.data.exportUploadGroup;
        window.location.href = signedDownloadUrl;
      } finally {
        this.downloadingUploadGroupIds = this.downloadingUploadGroupIds.filter(
          (id) => id !== uploadGroupId
        );
      }
    },
    async onDownloadInvalid(referenceFileId) {
      const response = await apolloClient.query({
        query: ExportInvalidRecords,
        variables: {
          referenceFileId,
        },
        fetchPolicy: 'network-only',
      });
      const {
        signedDownloadUrl,
      } = response.data.exportInvalidRecords;
      window.location.href = signedDownloadUrl;
    },
    confirmDeleteUploadGroup(uploadGroupId) {
      this.deleteConfirmId = uploadGroupId;
      this.deleteDialogOpen = true;
    },
    async onDeleteUploadGroup() {
      const uploadGroupId = this.deleteConfirmId;
      if (!uploadGroupId) return;
      // Dismiss the dialog up front; progress is shown on the group's own header.
      this.cancelDeleteUploadGroup();
      this.deletingUploadGroupIds.push(uploadGroupId);
      try {
        await this.$store.dispatch(
          'uploadReferences/deleteUploadGroup',
          uploadGroupId
        );
        this.showSnackbar({
          message: 'Upload group deleted successfully',
          color: 'success',
        });
      } catch (e) {
        reportError(e);
        this.showSnackbar({
          message: 'Failed to delete upload group. Please try again.',
          color: 'error',
        });
      } finally {
        this.deletingUploadGroupIds = this.deletingUploadGroupIds.filter(
          (id) => id !== uploadGroupId
        );
      }
    },
    cancelDeleteUploadGroup() {
      this.deleteDialogOpen = false;
      this.deleteConfirmId = null;
    },
    async onReRunDuplicates() {
      if (this.disableReRun) {
        const message = this.processingDuplicates
          ? 'Duplicate detection is already running for this review.'
          : 'Duplicate detection can be re-run after all uploads in this review have finished processing.';
        this.showSnackbar({ message, color: 'info' });
        return;
      }

      this.processingDuplicates = true;
      try {
        const response = await apolloClient.mutate({
          mutation: ProcessDuplicates,
          variables: {
            reviewId: this.reviewId,
          },
          refetchQueries: REFETCH_AFTER_DUPLICATE_CHANGE,
          // Hold the spinner until the refetches land so the breakdown the
          // user is looking at is already up to date when the snackbar shows.
          awaitRefetchQueries: true,
        });

        const unresolved =
          response.data.processDuplicates
            ?.unresolvedDuplicatesCount ?? 0;

        this.showSnackbar({
          message: unresolved
            ? `Duplicate detection re-run. ${unresolved} unresolved duplicate${
                unresolved === 1 ? '' : 's'
              } to review.`
            : 'Duplicate detection re-run. No unresolved duplicates.',
          color: 'success',
          timeout: 6000,
        });
      } catch (error) {
        this.showSnackbar({
          message:
            'Failed to re-run duplicate detection. Please try again.',
          color: 'error',
        });
      } finally {
        this.processingDuplicates = false;
      }
    },
    showSnackbar({ message, color = '', timeout = 5000 }) {
      this.snackbar = { show: true, message, color, timeout };
    },
  },
};
</script>
